import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { supabase } from '../config/supabase'
import { useAuth } from '../hooks/useAuth'

export default function Dashboard() {
  const navigate = useNavigate()
  const { isAdmin, loading: authLoading } = useAuth()
  const [stats, setStats] = useState({
    totalUndertakers: 0,
    approvedClaims: 0,
    globalBoosts: 0,
    loading: true,
  })

  useEffect(() => {
    // Redirect non-admin users to undertakers list
    if (!authLoading && !isAdmin) {
      navigate('/undertakers')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authLoading])

  useEffect(() => {
    if (isAdmin) {
      fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  const fetchStats = async () => {
    try {
      // Get total undertakers
      const { count: total } = await supabase
        .from('undertakers')
        .select('*', { count: 'exact', head: true })

      // Get approved claims
      const { count: approved } = await supabase
        .from('undertakers')
        .select('*', { count: 'exact', head: true })
        .eq('claim_approved', true)

      // Get global boosts
      const { count: boosts } = await supabase
        .from('undertakers')
        .select('*', { count: 'exact', head: true })
        .eq('global_boost', true)

      setStats({
        totalUndertakers: total || 0,
        approvedClaims: approved || 0,
        globalBoosts: boosts || 0,
        loading: false,
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
      setStats({ ...stats, loading: false })
    }
  }

  if (stats.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #2563eb 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Velkommen til Find Bedemand Admin 👋
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 5 }}>
        {/* Total Undertakers Card */}
        <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
          <Card 
            sx={{ 
              backgroundColor: 'primary.main',
              color: 'white',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontWeight: 500 }}>
                    Bedemænd i alt
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0 }}>
                    {stats.totalUndertakers}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Approved Claims Card */}
        <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontWeight: 500 }}>
                    Godkendte claims
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0 }}>
                    {stats.approvedClaims}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Global Boosts Card */}
        <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontWeight: 500 }}>
                    Global boosts
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0 }}>
                    {stats.globalBoosts}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Getting Started Card */}
      <Card 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: '1px solid',
          borderColor: 'rgba(37, 99, 235, 0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
            }}
          >
            🚀 Kom i gang
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Box component="li" sx={{ mb: 1.5 }}>
              <Typography variant="body1" color="text.primary">
                <strong>Administrer bedemænd:</strong> Opret eller importér bedemænd under "Bedemænd"
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1.5 }}>
              <Typography variant="body1" color="text.primary">
                <strong>Tegn service områder:</strong> Konfigurer service områder ved at tegne på kort
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1.5 }}>
              <Typography variant="body1" color="text.primary">
                <strong>Boost synlighed:</strong> Administrer boosts for at fremhæve specifikke bedemænd
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body1" color="text.primary">
                <strong>Godkend claims:</strong> Godkend anmodninger fra bedemænd der vil administrere deres profiler
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

